import { useState, useEffect } from "react";
import { apiMethods } from "../services/api";
import { useAuthStore } from "../stores/authStore";

export default function MenuPermissions() {
  const { isSuperAdmin, refreshUser } = useAuthStore();
  const [permissions, setPermissions] = useState({});
  const [availableMenus, setAvailableMenus] = useState({});
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isSuperAdmin()) {
      window.location.href = "/dashboard";
      return;
    }

    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [permissionsRes, menusRes, rolesRes] = await Promise.all([
        apiMethods.getMenuPermissions(),
        apiMethods.getAvailableMenus(),
        apiMethods.getRoles(),
      ]);

      setPermissions(permissionsRes.data || {});
      setAvailableMenus(menusRes.data || {});
      setRoles(rolesRes.data || []);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (roleName, menuKey) => {
    setPermissions((prev) => {
      const rolePerms = prev[roleName] || [];
      const updated = rolePerms.map((perm) =>
        perm.key === menuKey ? { ...perm, is_visible: !perm.is_visible } : perm
      );
      return { ...prev, [roleName]: updated };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates = [];

      Object.entries(permissions).forEach(([roleName, perms]) => {
        const role = roles.find((r) => r.name === roleName);
        if (role) {
          perms.forEach((perm) => {
            updates.push({
              role_id: role.id,
              menu_key: perm.key,
              is_visible: perm.is_visible,
            });
          });
        }
      });

      await apiMethods.updateMenuPermissions(updates);

      await refreshUser();

      alert("Menu permissions saved successfully");
    } catch (error) {
      alert("Failed to save permissions");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Menu Permissions</h1>
        <p className="mt-2 text-gray-600">
          Configure which menu items are visible to each role
        </p>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900">
              Role Permissions
            </h2>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Menu Item
                </th>
                {roles.map((role) => (
                  <th
                    key={role.id}
                    className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {role.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Object.keys(availableMenus).map((menuKey) => (
                <tr key={menuKey}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {availableMenus[menuKey]}
                  </td>
                  {roles.map((role) => {
                    const rolePerms = permissions[role.name] || [];
                    const perm = rolePerms.find((p) => p.key === menuKey);
                    const isVisible = perm?.is_visible ?? false;

                    return (
                      <td
                        key={role.id}
                        className="px-6 py-4 whitespace-nowrap text-center"
                      >
                        <button
                          onClick={() => handleToggle(role.name, menuKey)}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                            isVisible ? "bg-primary-600" : "bg-gray-200"
                          }`}
                          role="switch"
                          aria-checked={isVisible}
                        >
                          <span
                            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200 ease-in-out ${
                              isVisible ? "translate-x-5" : "translate-x-0"
                            }`}
                          />
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
